import { FC, Fragment, useEffect, useRef, useState } from "react";
import { SamsScope, samsScopes } from "@/models/samsVbl";
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { fetchMatchHistory, loadGameData } from "@/services/samsService";
import { PdfData } from "@/models/pdfData";
import {
  millisToDate,
  millisToDateAtMidnight,
} from "@/services/dateTimeService";
import { ExpandLess, ExpandMore, Pause, PlayArrow } from "@mui/icons-material";
import { buildGameStateFromHistory } from "@/services/gameState";
import { UpdateActionType } from "@/models/socketIo/updateAction";
import {
  getLocalActionHistory,
  removeLocalActionHistory,
  StorageMode,
} from "@/services/localActionHistory";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { useAppDataContext } from "@context/AppContext";

const StorageSelectionDialog: FC<{
  open: boolean;
  usedStorage: number;
  availableLocalStorage: number;
  handleClose: (selectedStorage: StorageMode) => void;
}> = ({ open, handleClose, usedStorage, availableLocalStorage }) => {
  const { onMobile } = useResponsiveContext();

  const { useLocalStorage } = useAppDataContext();

  const [selectedStorage, setSelectedStorage] = useState<StorageMode>(
    useLocalStorage ? "local" : "extended",
  );
  const [persistentStoragePermission, setPersistentStoragePermission] =
    useState<PermissionState | "pending">("pending");

  useEffect(() => {
    navigator.permissions
      .query({ name: "persistent-storage" })
      .then((status) => {
        setPersistentStoragePermission(status.state);
      });
  }, []);

  const localStorageDisabled = availableLocalStorage < usedStorage;

  const handleStorageChange = async (
    event: React.MouseEvent<HTMLElement>,
    newStorage: "local" | "extended",
  ) => {
    if (newStorage !== null) {
      if (newStorage === "local") {
        setSelectedStorage(newStorage);
      } else {
        const storagePermissionStatus = await navigator.permissions.query({
          name: "persistent-storage",
        });
        if (storagePermissionStatus.state === "granted") {
          setSelectedStorage(newStorage);
        } else if (storagePermissionStatus.state === "prompt") {
          const isPersisted = await navigator.storage.persist();
          if (isPersisted) {
            setSelectedStorage(newStorage);
          } else {
            setPersistentStoragePermission("denied");
          }
        }
      }
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      fullScreen={onMobile}
      maxWidth={"sm"}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>Speicher auswählen</DialogTitle>

      <DialogContent>
        <Typography>
          Browser haben einen begrenzten Speicherplatz, den eine Website ohne
          explizite Erlaubnis nutzen darf (der sogenannte "Local Storage"). Wenn
          dieser Speicherplatz erschöpft ist, hast du zwei Möglichkeiten:
        </Typography>
        <Typography
          sx={{ marginTop: 2, textIndent: "-1.1em", paddingLeft: "1.1em" }}
        >
          {`1. Local Storage ${useLocalStorage ? "weiter " : ""}nutzen: Lösche Spiele aus der Liste. Über das
          "Plus" Icon unten rechts kannst du auch alle Spiele löschen.`}
        </Typography>
        <Typography
          sx={{ marginTop: 2, textIndent: "-1.1em", paddingLeft: "1.1em" }}
        >
          2. Speichernutzung erlauben: Du kannst dem Browser erlauben, mehr
          Speicherplatz für diese Website zu reservieren. Wenn du diese Option
          wählst, bittet der Browser unter umständen um die Erlaubnis dazu.
        </Typography>
        <Divider sx={{ marginTop: 2 }} />
        <Typography sx={{ marginTop: 2 }}>
          Welche Speicheroption möchtest du nutzen?
        </Typography>

        <ToggleButtonGroup
          color={"primary"}
          sx={{ marginTop: 1, width: "100%", justifyContent: "center" }}
          exclusive
          value={selectedStorage}
          onChange={handleStorageChange}
        >
          <ToggleButton value={"local"} disabled={localStorageDisabled}>
            Local Storage
          </ToggleButton>
          <ToggleButton
            value={"extended"}
            disabled={["pending", "denied"].includes(
              persistentStoragePermission,
            )}
          >
            {persistentStoragePermission === "pending"
              ? "Prüfe Browser"
              : "Mehr Speicher erlauben"}
          </ToggleButton>
        </ToggleButtonGroup>
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"center"}
          sx={{ marginTop: 2 }}
        >
          {localStorageDisabled && (
            <Typography variant={"caption"}>
              Nicht genug Speicherplatz für "Local Storage" verfügbar, du musst
              zuerst Spiele löschen um zurück auf "Local Storage" wechseln zu
              können
            </Typography>
          )}
          {persistentStoragePermission === "denied" && (
            <Typography variant={"caption"}>
              Der Browser hat die Erlaubnis für mehr Speicherplatz verweigert.
              Du musst das entweder in den Einstellungen des Browser ändern,
              oder weiterhin "Local Storage" nutzen.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(selectedStorage)}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StorageSelectionDialog;
